import { firstTag } from "./tags.ts";
import type { OsmTags } from "./types.ts";

export function osmAddressLines(tags: OsmTags): string[] {
  // Preserve local address formatting when mapped explicitly.
  // https://wiki.openstreetmap.org/wiki/Addresses#Japan
  const fullAddress = linesFromTag(tags["addr:full"]);
  if (fullAddress.length > 0) return fullAddress;

  const lines: string[] = [];
  // Some addresses intentionally have no street; addr:place carries the
  // street-like part in villages, islands, subdivisions, and similar places.
  // https://wiki.openstreetmap.org/wiki/Addresses#Addresses_without_street_names
  const placeOrStreet = firstTag(tags, ["addr:street", "addr:place"]);
  const streetLine = [placeOrStreet, tags["addr:housenumber"]]
    .filter(hasText)
    .join(" ");
  const unitLine = addressUnitLine(tags);
  const cityLine = [
    tags["addr:postcode"],
    tags["addr:city"],
    tags["addr:state"],
  ]
    .filter(hasText)
    .join(" ");

  if (hasText(tags["addr:housename"])) lines.push(tags["addr:housename"]);
  if (streetLine) lines.push(streetLine);
  if (unitLine) lines.push(unitLine);
  if (cityLine) lines.push(cityLine);
  if (hasText(tags["addr:country"])) lines.push(tags["addr:country"]);

  return lines;
}

function addressUnitLine(tags: OsmTags): string | undefined {
  // Secondary address tags are common for multi-tenant or multi-unit places.
  // https://wiki.openstreetmap.org/wiki/Addresses#Multiple_buildings_for_one_housenumber
  // https://wiki.openstreetmap.org/wiki/Addresses#United_States
  const parts = [
    addressPart("Unit", tags["addr:unit"]),
    addressPart("Flats", tags["addr:flats"]),
    addressPart("Floor", tags["addr:floor"]),
    addressPart("Room", tags["addr:room"]),
  ].filter(hasText);

  return parts.length > 0 ? parts.join(", ") : undefined;
}

function addressPart(
  label: string,
  value: string | undefined,
): string | undefined {
  return hasText(value) ? `${label} ${value.trim()}` : undefined;
}

function linesFromTag(value: string | undefined): string[] {
  return value?.split(/\r?\n/).map((line) => line.trim()).filter(hasText) ?? [];
}

function hasText(value: string | undefined): value is string {
  return Boolean(value?.trim());
}
